import React, { useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';

import cs from 'classnames';
import moment from 'moment-timezone';
import { useRecoilValue } from 'recoil';

import { TableContextProvider } from 'lib/context/TableContext/TableContextProvider';
import { ia, iaRa } from 'lib/helpers/utility';
import { usePatientAppointments } from 'lib/hooks/queries/patients/usePatientAppointments';

import EncounterNavigator from 'components/practice/NewAppointment/EncounterNavigator';
import { useAppointmentPreview } from 'components/practice/NewAppointment/hooks/useAppointmentPreview';
import {
  appointmentStatuses as aptStatuses,
  currentPractice
} from 'components/practice/practiceState';
import AGTable from 'components/shared/AGTable/AGTable';
import { CustomStatusBarPagination } from 'components/shared/AGTable/CustomStatusBarPagination';
import ErrorBoundary from 'components/shared/Error/Boundary';

import Skeleton from '../../../shared/Skeleton/Skeleton';

import getDefaultColumns from './lib/defaultColumns';

export default function PastAppointments({ props }) {
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const practice = useRecoilValue(currentPractice);
  const appointmentStatuses = useRecoilValue(aptStatuses);
  const [paginationState, setPaginationState] = useState({
    page: 1,
    pageCategory: 'first',
    dateRange: { startDate: new Date(), endDate: new Date() },
    limit: 15
  });

  const outletContext = useOutletContext();

  const { patient, setUpcomingCounter, setPastCounter, upcomingCounter, pastCounter } =
    props?.patient ? props : outletContext;

  const withCount = paginationState.page === 1 && !pastCounter && !upcomingCounter;

  const { pathname, appointment_id, openAppointment } =
    useAppointmentPreview(setSelectedAppointment);

  const { data, isLoading } = usePatientAppointments({
    params: {
      patientId: patient?.id,
      withCount,
      ...paginationState
    },
    type: 'upcoming',
    dependencies: [patient?.id, paginationState.page, pathname]
  });

  const appointments = iaRa(data?.appointments);

  useEffect(() => {
    setUpcomingCounter && setUpcomingCounter((prev) => data?.upcomingCount || prev);
    setPastCounter && setPastCounter((prev) => data?.pastCount || prev);

    if (ia(appointments)) {
      setPaginationState((prev) => ({
        ...prev,
        dateRange: {
          startDate: appointments[appointments.length - 1].starts_at,
          endDate: appointments[0].starts_at
        }
      }));
    }
  }, [data]);

  const navigate = useNavigate();
  moment.tz.setDefault(practice?.timezone);

  const hideModal = () => {
    setSelectedAppointment(null);
    if (appointment_id) navigate(pathname, { replace: true });
  };

  const onPageChange = (newPage) => {
    const page = paginationState.page;
    if (newPage === page) return;

    setPaginationState((prev) => ({ ...prev, page: newPage }));

    if (newPage > page + 1) {
      const limitChange = upcomingCounter % 15;
      setPaginationState((prev) => ({ ...prev, pageCategory: 'last', limit: limitChange }));
    } else if (newPage > page) {
      setPaginationState((prev) => ({ ...prev, pageCategory: 'forward', limit: 15 }));
    } else if (newPage < page - 1) {
      setPaginationState((prev) => ({ ...prev, pageCategory: 'first', limit: 15 }));
    } else {
      setPaginationState((prev) => ({ ...prev, pageCategory: 'back', limit: 15 }));
    }
  };

  const columnDefs = getDefaultColumns(appointmentStatuses, practice?.timezone);

  return (
    <TableContextProvider cols={columnDefs}>
      <ErrorBoundary>
        <div className="flex h-full flex-col">
          <div className="flex-1">
            {isLoading ? (
              <Skeleton count={13} height={48} />
            ) : (
              <div className={cs('flex h-full flex-col overflow-hidden !pb-0')}>
                <div className="ag-theme-quartz !mb-0  h-full">
                  <AGTable
                    data={appointments}
                    defaultColDef={columnDefs}
                    suppressRowClickSelection={true}
                    onRowClicked={({ data }) => openAppointment(data)}
                    customClassName="ag-grid-interactive"
                  />
                </div>
              </div>
            )}

            {selectedAppointment && (
              <EncounterNavigator
                isOpen={!!selectedAppointment}
                handleClose={hideModal}
                appointment={selectedAppointment}
              />
            )}
          </div>
          <div className="flex h-[50px] justify-end !px-2">
            <CustomStatusBarPagination
              data={appointments}
              page={paginationState.page}
              count={upcomingCounter}
              limit={15}
              onPageChange={onPageChange}
            />
          </div>
        </div>
      </ErrorBoundary>
    </TableContextProvider>
  );
}
